import React, { useState, useRef, useEffect } from "react";
import { useUI } from "../../context/UIContext";
import { runShell } from "../../api";

const SENT = "##__CWD__##:"; 

function wrapCmd(userCmd, shell, cwd) {
  if (shell === "cmd") {
    const cd = cwd ? `cd /d "${cwd}" & ` : "";
    return `${cd}${userCmd} & for /f "delims=" %i in ('cd') do @echo ${SENT}%i`;
  }
  const q = cwd ? `'${cwd.replace(/'/g, "''")}'` : null;
  const cd = q ? `Set-Location -LiteralPath ${q} -EA SilentlyContinue; ` : "";
  return `${cd}${userCmd}\nWrite-Output ("${SENT}" + (Get-Location).Path)`;
}

function parseOut(raw) {
  if (!raw) return { text: "", cwd: null };
  const lines = raw.split(/\r?\n/);
  let cwd = null;
  const kept = [];
  for (const l of lines) {
    const i = l.indexOf(SENT);
    if (i >= 0) cwd = l.slice(i + SENT.length).trim();
    else kept.push(l);
  }
  return { text: kept.join("\n").replace(/\s+$/, ""), cwd };
}

export default function ShellModal({ serverCode }) {
  const { closeModal, toast } = useUI();
  const [cmd, setCmd] = useState("");
  const [shell, setShell] = useState("powershell"); 
  const [cwd, setCwd] = useState(""); 
  const [history, setHistory] = useState([]); 
  const [busy, setBusy] = useState(false);

  const cmdLog = useRef([]); 
  const histIdx = useRef(-1);
  const outRef = useRef(null);
  const inpRef = useRef(null);

  useEffect(() => {
    if (outRef.current) outRef.current.scrollTop = outRef.current.scrollHeight;
  }, [history, busy]);

  const send = async (raw) => {
    const c = (raw != null ? raw : cmd).trim();
    if (!c || busy) return;
    if (!serverCode) return toast("Thiếu serverCode", "err");
    cmdLog.current.push(c);
    histIdx.current = cmdLog.current.length;
    const promptCwd = cwd || "~";
    setBusy(true);
    setCmd("");
    try {
      const { ok, data } = await runShell(serverCode, wrapCmd(c, shell, cwd), shell);
      if (ok) {
        const parsed = parseOut(data.out || "");
        if (parsed.cwd) setCwd(parsed.cwd);
        setHistory((h) => [
          ...h,
          { cmd: c, out: parsed.text, err: data.error || "", shell, cwd: promptCwd },
        ]);
      } else {
        setHistory((h) => [
          ...h,
          { cmd: c, out: "", err: data.message || "Lỗi server", shell, cwd: promptCwd },
        ]);
      }
    } catch {
      setHistory((h) => [
        ...h,
        { cmd: c, out: "", err: "Không kết nối được BackendNode", shell, cwd: promptCwd },
      ]);
    } finally {
      setBusy(false);
      if (inpRef.current) inpRef.current.focus();
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") send();
    else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!cmdLog.current.length) return;
      histIdx.current = Math.max(0, histIdx.current - 1);
      setCmd(cmdLog.current[histIdx.current] || "");
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!cmdLog.current.length) return;
      histIdx.current = Math.min(cmdLog.current.length, histIdx.current + 1);
      setCmd(cmdLog.current[histIdx.current] || "");
    } else if (e.key === "l" && e.ctrlKey) {
      e.preventDefault();
      setHistory([]);
    }
  };

  const checkAdmin = () => {
    const c =
      shell === "cmd"
        ? "net session >nul 2>&1 && echo ADMIN: co quyen quan tri || echo KHONG co quyen admin"
        : "if (([Security.Principal.WindowsPrincipal][Security.Principal.WindowsIdentity]::GetCurrent()).IsInRole([Security.Principal.WindowsBuiltinRole]::Administrator)) { 'ADMIN: co quyen quan tri' } else { 'KHONG co quyen admin' }; whoami";
    send(c);
  };

  const promptLabel = (d) => `${shell === "cmd" ? "" : "PS "}${d}>`;

  const ShellBtn = ({ value, label }) => {
    const isAct = shell === value;
    return (
      <button
        onClick={() => setShell(value)}
        style={{
          padding: "5px 12px",
          borderRadius: "8px",
          fontSize: "12px",
          fontWeight: 600,
          cursor: "pointer",
          border: isAct ? "none" : "1px solid var(--border2)",
          background: isAct ? "var(--primary)" : "transparent",
          color: isAct ? "#fff" : "var(--dim)",
          transition: "all 0.15s ease",
        }}
        className="shell-tab-btn"
      >
        {label}
      </button>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
      {/* Terminal Title Header */}
      <div
        style={{
          marginBottom: "12px",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          fontSize: "12px",
          color: "var(--dim)",
          fontFamily: "var(--font-mono)",
        }}
      >
        <span>
          Terminal remote: <strong style={{ color: "var(--text)" }}>{serverCode}</strong>
        </span>
        <div style={{ marginLeft: "auto", display: "flex", gap: "6px" }}>
          <ShellBtn value="powershell" label="PowerShell" />
          <ShellBtn value="cmd" label="CMD" />
        </div>
      </div>

      {/* Terminal logs window */}
      <div
        className="terminal-window scroll"
        ref={outRef}
        style={{ height: "360px", maxHeight: "55vh" }}
      >
        {history.length === 0 ? (
          <div style={{ color: "var(--faint)", fontSize: "12.5px" }}>
            {promptLabel(cwd || "C:\\")} <span className="shell-cursor" style={{ animation: "caret 1s infinite", color: "var(--blue)" }}>█</span>
            <div style={{ marginTop: "12px", fontSize: "11px", color: "var(--faint)", lineHeight: "1.5" }}>
              Gõ lệnh rồi Enter · Phím ↑/↓ để gọi lại lịch sử lệnh cũ · Ctrl+L để xoá màn hình · Thao tác cd được ghi nhớ tự động giữa các lệnh.
            </div>
          </div>
        ) : (
          history.map((h, i) => (
            <div key={i} style={{ marginBottom: "8px" }}>
              <div style={{ color: "var(--blue)", fontWeight: 600 }}>
                {h.shell === "cmd" ? `${h.cwd}>` : `PS ${h.cwd}>`} {h.cmd}
              </div>
              {h.out && <div style={{ whiteSpace: "pre-wrap", color: "var(--text)", marginTop: "2px" }}>{h.out}</div>}
              {h.err && (
                <div style={{ whiteSpace: "pre-wrap", color: "var(--red)", marginTop: "2px" }}>
                  {h.err}
                </div>
              )}
            </div>
          ))
        )}
        {busy && <div style={{ color: "var(--amber)", fontFamily: "var(--font-mono)", fontSize: "12.5px" }}>Đang chạy lệnh...</div>}
      </div>

      {/* Inputs CommandLine row */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "12px" }}>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "12px",
            color: "var(--blue)",
            whiteSpace: "nowrap",
            maxWidth: "220px",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={cwd || "C:\\"}
        >
          {promptLabel(cwd || "C:\\")}
        </span>
        <input
          className="field-inp"
          ref={inpRef}
          style={{ flex: 1, margin: 0, fontFamily: "var(--font-mono)", height: "38px" }}
          type="text"
          value={cmd}
          placeholder={
            shell === "cmd"
              ? "dir, ipconfig, tasklist, cd ..."
              : "Get-ChildItem, Get-Service, cd ..."
          }
          autoFocus
          disabled={busy}
          onChange={(e) => setCmd(e.target.value)}
          onKeyDown={onKeyDown}
        />
        <button
          className="mbtn mbtn-primary"
          onClick={() => send()}
          disabled={busy}
          style={{ height: "38px", padding: "0 18px" }}
        >
          {busy ? "…" : "▶ Chạy"}
        </button>
      </div>

      {/* Extra options bottom */}
      <div className="modal-footer" style={{ marginTop: "15px" }}>
        <button className="mbtn mbtn-cancel" onClick={checkAdmin} disabled={busy}>
          Kiểm tra quyền
        </button>
        <button className="mbtn mbtn-cancel" onClick={() => setHistory([])}>
          Xoá màn hình
        </button>
        <button className="mbtn mbtn-cancel" onClick={closeModal}>
          Đóng
        </button>
      </div>
      
      <style>{`
        .shell-tab-btn:hover {
          border-color: var(--dim) !important;
        }
      `}</style>
    </div>
  );
}
